import './global.scss';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { ClickAnalyticsPlugin } from '@microsoft/applicationinsights-clickanalytics-js';
import {
  AppInsightsErrorBoundary,
  ReactPlugin,
  withAITracking,
} from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ThemeProvider } from '@mui/material';
import { createBrowserHistory } from 'history';
import React, { Suspense, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { pdfjs } from 'react-pdf';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { Router } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { PersistGate } from 'redux-persist/integration/react';
import { getUserData } from 'services/auth.services';
import history from 'src/app/router/utils/history';
import { Auth0ProviderWithNavigate } from 'src/Auth0Provider';
import { generateAzureStorageBlobToken } from 'src/helper/utilityFunctions';
import { setAccessControlPermissions, setSaasToken } from 'src/redux/store/slices/auth';
import { theme } from 'src/theme';
import { appInsightUserAgent, enumUserRolesTokenApi } from 'src/utils/constants';

import NoServer from './app/public/pages/noServer';
import getRouteConfigs from './app/router/config/base.route';
import generateRoutesFromConfig from './app/router/utils/generateRoutesFromConfig';
import { isObjectEmpty, mainDomain } from './helper/utilityFunctions';
import store, { persistor } from './redux/store/index';
import { setTenantInfo } from './redux/store/slices/auth';
import { MULTI_TENANT_AUTH } from './utils/constants/multiTanentAuthInfo';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const browserHistory = createBrowserHistory({ basename: window.location.origin });

let clickPluginInstance = new ClickAnalyticsPlugin();
let clickPluginConfig = {
  autoCapture: true,
};

const reactPlugin = new ReactPlugin();

const appInsights = new ApplicationInsights({
  config: {
    connectionString: `instrumentationKey=${process.env.REACT_APP_INSIGHTS}`,
    enableAutoRouteTracking: true,
    disableAjaxTracking: false,
    autoTrackPageVisitTime: true,
    enableCorsCorrelation: true,
    enableRequestHeaderTracking: true,
    enableResponseHeaderTracking: true,
    correlationHeaderExcludedDomains: [
      '*.auth0.com',
      '*.googleapis.com',
      '*.blob.core.windows.net',
    ],
    extensions: [reactPlugin, clickPluginInstance],

    [clickPluginInstance.identifier]: clickPluginConfig,

    extensionConfig: {
      [reactPlugin.identifier]: { history: browserHistory },
    },
  },
});

appInsights.loadAppInsights();

appInsights.addTelemetryInitializer((envelope) => {
  envelope.data.baseData.properties.user_agent = appInsightUserAgent;
});

const Main = () => {
  const { i18n } = useTranslation();

  const currentLanguage = useSelector((state) => state.auth.currentLanguage);
  const accessToken = useSelector((state) => state.auth.accessToken);
  const franchiseId = useSelector((state) => state.auth.franchiseId);
  const userRole = useSelector((state) => state.auth.userRole);
  const sassToken = useSelector((state) => state.auth.saasToken);
  const tenant = mainDomain();
  const currentDomain = MULTI_TENANT_AUTH[tenant];

  const dispatch = useDispatch();
  const tenantInfo = useSelector((state) => state.auth.tenantInfo);

  useEffect(() => {
    i18n.changeLanguage(currentLanguage?.code);

    if (isObjectEmpty(tenantInfo)) {
      dispatch(setTenantInfo({ loader: currentDomain.loader, logo: currentDomain.logo }));
    }
  }, []);

  useEffect(() => {
    i18n.changeLanguage(currentLanguage?.code);
  }, []);

  const _getPermission = async () => {
    try {
      const response = await getUserData();
      if (response && response.statusCode == 200) {
        dispatch(setAccessControlPermissions(response?.data?.user?.accessControlList));
      }
    } catch (error) {
      console.log('test permission');
    }
  };

  useEffect(() => {
    if (
      (accessToken && franchiseId && userRole) ||
      (userRole && accessToken && enumUserRolesTokenApi.includes(userRole?.slug))
    ) {
      (async () => {
        if (!sassToken) {
          const saasToken = await generateAzureStorageBlobToken({
            permissions: ['w', 'r', 'd'],
          });

          dispatch(setSaasToken(saasToken));
        }
      })();
    }

    // if (accessToken) {
    //   getPermission();
    // }
  }, [franchiseId, accessToken, userRole, sassToken]);

  const configs = useMemo(getRouteConfigs, []);

  // Generate and return routes from configs
  return useMemo(() => generateRoutesFromConfig(configs), [configs]);
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <Provider store={store}>
        <PersistGate loading={null} persistor={persistor}>
          <Router history={history}>
            <AppInsightsErrorBoundary onError={NoServer} appInsights={reactPlugin}>
              <Suspense fallback={null}>
                <Auth0ProviderWithNavigate>
                  <Main />
                </Auth0ProviderWithNavigate>
              </Suspense>
            </AppInsightsErrorBoundary>
          </Router>
        </PersistGate>
      </Provider>
      <ToastContainer />
    </ThemeProvider>
  );
}

export default withAITracking(reactPlugin, App);
